import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  useDeleteStockLocation,
  useStockLocations,
  useUpsertStockLocation,
} from "@/features/locations/api";
import { LOCATION_TYPES, labelFor } from "@/features/locations/constants";
import {
  emptyLocationForm,
  locationFormSchema,
  type LocationFormValues,
} from "@/features/locations/schema";
import type { StockLocationListRow } from "@/features/locations/types";
import { isSupabaseConfigured } from "@/lib/supabase";

export function LocationsPage() {
  return (
    <ErrorBoundary title="Locations page crashed">
      <LocationsPageInner />
    </ErrorBoundary>
  );
}

function LocationsPageInner() {
  const { data = [], isLoading, error, refetch, isFetching } = useStockLocations();
  const upsert = useUpsertStockLocation();
  const remove = useDeleteStockLocation();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [editing, setEditing] = useState<StockLocationListRow | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<StockLocationListRow | null>(null);

  const form = useForm<LocationFormValues>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: emptyLocationForm,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter === "active" && !row.is_active) return false;
      if (statusFilter === "inactive" && row.is_active) return false;
      if (typeFilter !== "all" && row.location_type !== typeFilter) return false;
      if (!q) return true;
      return (
        row.name.toLowerCase().includes(q) ||
        row.code.toLowerCase().includes(q) ||
        (row.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, search, typeFilter, statusFilter]);

  const parentOptions = useMemo(
    () => data.filter((row) => row.id !== editing?.id && row.is_active),
    [data, editing?.id],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    const nextSort = data.length ? Math.max(...data.map((d) => d.sort_order)) + 10 : 10;
    form.reset({ ...emptyLocationForm, sort_order: nextSort });
    setOpen(true);
  }, [data, form]);

  useMasterListShortcuts({
    searchInputId: "locations-search",
    onCreate: openCreate,
  });

  function openEdit(row: StockLocationListRow) {
    setEditing(row);
    form.reset({
      code: row.code,
      name: row.name,
      location_type: row.location_type,
      parent_id: row.parent_id ?? "",
      description: row.description ?? "",
      is_active: row.is_active,
      sort_order: row.sort_order,
    });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    if (editing && values.parent_id === editing.id) {
      form.setError("parent_id", { message: "A location cannot be its own parent" });
      return;
    }
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  const empty = !isLoading && !error && data.length === 0;

  return (
    <MasterPage>
      <MasterHeader
        title="Locations"
        icon={<MapPin />}
        countLabel={`${data.length} places`}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          <Button type="button" size="sm" onClick={openCreate}>
            <Plus />
            New location
          </Button>
        }
      />

      {!isSupabaseConfigured ? (
        <MasterBanner>Connect Supabase environment variables to enable live data.</MasterBanner>
      ) : null}

      {error ? <MasterBanner tone="danger">{(error as Error).message}</MasterBanner> : null}

      {empty ? (
        <EmptyState
          title="No locations yet"
          description="Seed includes yards, sheds and bays — or create the first plant location."
          action={
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus />
              Create location
            </Button>
          }
        />
      ) : (
        <>
          <MasterFilterBar shownLabel={`${filtered.length} shown`}>
            <MasterSearchInput
              id="locations-search"
              value={search}
              onChange={setSearch}
              placeholder="Search code or name…  (/)"
            />
            <NativeSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="w-28"
              aria-label="Status filter"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </NativeSelect>
            <NativeSelect
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-32"
              aria-label="Type filter"
            >
              <option value="all">All types</option>
              {LOCATION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </NativeSelect>
          </MasterFilterBar>

          <div className="overflow-auto rounded-md border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-[0.05em] text-muted-foreground">
                  <th className="px-2 py-1.5 font-semibold">Code</th>
                  <th className="px-2 py-1.5 font-semibold">Name</th>
                  <th className="px-2 py-1.5 font-semibold">Type</th>
                  <th className="px-2 py-1.5 font-semibold">Parent</th>
                  <th className="px-2 py-1.5 font-semibold">Status</th>
                  <th className="px-2 py-1.5 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-8 text-center text-xs text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-8 text-center text-xs text-muted-foreground">
                      No matching locations
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr
                      key={row.id}
                      className="group cursor-pointer border-b border-border/70 hover:bg-muted/40"
                      onClick={() => openEdit(row)}
                    >
                      <td className="px-2 py-1 font-mono text-xs font-medium">{row.code}</td>
                      <td className="px-2 py-1">
                        <div className="font-medium leading-tight">{row.name}</div>
                        {row.description ? (
                          <div className="truncate text-2xs text-muted-foreground">
                            {row.description}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-2 py-1">
                        <Badge variant="outline">{labelFor(LOCATION_TYPES, row.location_type)}</Badge>
                      </td>
                      <td className="px-2 py-1 text-xs text-muted-foreground">
                        {row.parent ? `${row.parent.code}` : "—"}
                      </td>
                      <td className="px-2 py-1">
                        <StatusDot tone={row.is_active ? "success" : "neutral"}>
                          {row.is_active ? "Active" : "Inactive"}
                        </StatusDot>
                      </td>
                      <td className="px-2 py-1 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Edit ${row.name}`}
                          onClick={() => openEdit(row)}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Delete ${row.name}`}
                          className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                          onClick={() => setDeleting(row)}
                        >
                          <Trash2 />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit location" : "New location"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field
                label="Code"
                required
                span={4}
                error={form.formState.errors.code?.message}
                htmlFor="loc-code"
              >
                <Input id="loc-code" className="uppercase" {...form.register("code")} />
              </Field>
              <Field
                label="Name"
                required
                span={8}
                error={form.formState.errors.name?.message}
                htmlFor="loc-name"
              >
                <Input id="loc-name" {...form.register("name")} />
              </Field>
              <Field label="Type" span={4} htmlFor="loc-type">
                <NativeSelect id="loc-type" {...form.register("location_type")}>
                  {LOCATION_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label="Parent"
                span={4}
                error={form.formState.errors.parent_id?.message}
                hint="Optional zone / area"
                htmlFor="loc-parent"
              >
                <NativeSelect id="loc-parent" {...form.register("parent_id")}>
                  <option value="">None</option>
                  {parentOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Sort order" span={4} htmlFor="loc-sort">
                <Input id="loc-sort" type="number" {...form.register("sort_order")} />
              </Field>
              <ToggleField
                label="Active"
                description="Inactive locations stay in history but are hidden from new docs"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v)}
                span={12}
              />
              <Field label="Description" span={12} htmlFor="loc-desc">
                <Input id="loc-desc" {...form.register("description")} />
              </Field>
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete location?"
        description={
          deleting
            ? `${deleting.name} will be removed. Prefer deactivate if stock was stored here.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </MasterPage>
  );
}
